import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Phone, 
  Star,
  Award,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Clock,
  Headphones,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/shared/ContactForm';
import ServiceCard from '../components/shared/ServiceCard';
import { 
  companyInfo, 
  images, 
  services, 
  whyChooseUs, 
  testimonials 
} from '../data/mock';

const iconMap = {
  Award,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Clock,
  Headphones
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Parquet in legno"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full mb-6">
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              <span className="text-amber-300 text-sm font-medium">Artigiani del legno dal 1990</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {companyInfo.tagline}
            </h1>
            
            <p className="text-xl text-slate-200 mb-8 leading-relaxed">
              {companyInfo.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/preventivi">
                <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-base bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl transition-all">
                  Richiedi Preventivo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
              >
                <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-base bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                  <Phone className="mr-2 w-5 h-5" />
                  Chiamaci
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-amber-400" />
                <span className="text-white/90 text-sm">Garanzia 10 anni</span>
              </div>
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-6 h-6 text-amber-400" />
                <span className="text-white/90 text-sm">Materiali certificati</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-amber-400" />
                <span className="text-white/90 text-sm">Preventivo gratuito</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.minimalist}
                  alt="Pavimento in parquet G&B Wood"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-amber-100 rounded-2xl -z-10" />
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-amber-600/10 rounded-2xl -z-10" />
              
              {/* Stats Card */}
              <div className="absolute bottom-8 left-8 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Award className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-800">30+</p>
                    <p className="text-slate-500 text-sm">Anni di esperienza</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
                Chi Siamo
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6 leading-tight">
                Passione per il legno, dedizione al cliente
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                G&B Wood nasce dalla passione di Carmine Avilio per il legno. Da oltre 30 anni 
                trasformo gli spazi con la bellezza senza tempo dei pavimenti in legno, 
                unendo tecniche artigianali e materiali di prima qualità.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Ogni progetto è per me un'opera d'arte. Dalla scelta del legno alla posa finale, 
                seguo ogni fase con cura meticolosa per garantire risultati che durano nel tempo.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Qualità garantita</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Puntualità</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Trasparenza</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Professionalità</span>
                </div>
              </div>

              <Link to="/chi-siamo">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                  Scopri la nostra storia
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              I Nostri Servizi
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
              Soluzioni complete per ogni esigenza
            </h2>
            <p className="text-slate-600 text-lg">
              Dalla posa del parquet alla levigatura, dalle porte interne ai trattamenti protettivi: 
              un servizio completo per valorizzare i tuoi spazi.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {services.slice(0, 8).map((service) => (
              <ServiceCard key={service.id} service={service} compact />
            ))}
          </div>

          <div className="text-center">
            <Link to="/servizi">
              <Button size="lg" variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
                Vedi tutti i servizi
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Perché Sceglierci
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              La qualità che fa la differenza
            </h2>
            <p className="text-slate-300 text-lg">
              Scegliere G&B Wood significa affidarsi a professionisti che mettono la tua 
              soddisfazione al primo posto.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon] || Award;
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 transition-colors">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent className="w-7 h-7 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Testimonianze
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
              Cosa dicono i nostri clienti
            </h2>
            <p className="text-slate-600 text-lg">
              La soddisfazione dei nostri clienti è la nostra migliore presentazione.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-amber-700 font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-amber-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Pronto a trasformare i tuoi spazi?
              </h2>
              <p className="text-amber-100 text-lg mb-8 leading-relaxed">
                Richiedi un preventivo gratuito e senza impegno. Ti contatteremo entro 24 ore 
                per discutere del tuo progetto e fissare un sopralluogo.
              </p>
              <div className="flex flex-wrap gap-6">
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-100">Chiamaci</p>
                    <p className="font-semibold">{companyInfo.phone}</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Richiedi Preventivo Gratuito</h3>
              <ContactForm variant="dark" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
